import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Building2, HardHat, Hammer, ShieldCheck, MapPin, Phone, Mail, Award, Clock, Users, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY, SERVICES, VALUES } from "@/lib/site";
import { api } from "@/lib/api";

export default function HomePage() {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);

  const featured = projects.filter((p) => p.featured).slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center text-white overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_fiji-asset-builds/artifacts/54wcpeu2_8e2478dc-b178-496b-8590-a22bb19036d9.jpeg"
            alt="Asset Group Construction Site"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0A1A36]/82" />
          <div className="absolute inset-0 ag-grid-lines opacity-50" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8 ag-fade-up">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#D4AF37]" />
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#D4AF37]">
                Construction · Civil Works · Fiji
              </span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tighter">
              We build strong <br />
              <span className="text-[#D4AF37]">assets</span> for a <br />
              prosperous future.
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-white/80 max-w-2xl leading-relaxed">
              Asset Group Pte Ltd delivers residential, commercial and civil construction
              across Fiji with disciplined craft, dependable timelines, and a commitment
              to the structures that hold our communities together.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/quote" data-testid="hero-quote-button">
                <Button className="rounded-none h-12 px-7 bg-[#D4AF37] hover:bg-[#b8952b] text-[#0A1A36] font-bold uppercase tracking-wider ag-btn-hover">
                  Request a Quote <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/services" data-testid="hero-services-button">
                <Button variant="outline" className="rounded-none h-12 px-7 bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0A1A36] font-bold uppercase tracking-wider ag-btn-hover">
                  View Our Services
                </Button>
              </Link>
              <Link to="/contact" data-testid="hero-contact-button">
                <Button variant="ghost" className="rounded-none h-12 px-7 text-white hover:bg-white/10 font-bold uppercase tracking-wider ag-btn-hover">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>

          {/* Spec card */}
          <div className="lg:col-span-4 ag-fade-up" style={{ animationDelay: "150ms" }}>
            <div className="border border-[#D4AF37]/40 bg-[#0A1A36]/60 backdrop-blur p-7">
              <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-5">Project Sheet</div>
              <div className="space-y-4 text-sm">
                <Row k="Established in" v="Suva, Fiji" />
                <Row k="Disciplines" v="Building · Civil" />
                <Row k="Coverage" v="Viti Levu & beyond" />
                <Row k="Standard" v="Quality. Safety. Reliability." />
              </div>
              <div className="mt-6 pt-6 border-t border-white/10 text-white/80 text-sm">
                <div className="flex items-center gap-2 mb-1"><Phone className="h-4 w-4 text-[#D4AF37]" /> {COMPANY.phone}</div>
                <div className="flex items-center gap-2 break-all"><Mail className="h-4 w-4 text-[#D4AF37]" /> {COMPANY.email}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES STRIP */}
      <section className="bg-white border-b border-[#E2E8F0]" data-testid="home-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3">
          {VALUES.map((v, i) => (
            <div
              key={v.num}
              className={`py-10 md:py-14 px-2 md:px-8 ${i < 2 ? "md:border-r border-[#E2E8F0]" : ""} ${i < 2 ? "border-b md:border-b-0 border-[#E2E8F0]" : ""}`}
            >
              <div className="text-xs font-mono text-[#D4AF37]">{v.num}</div>
              <div className="mt-2 font-display text-3xl font-extrabold tracking-tight">{v.title}.</div>
              <p className="mt-3 text-slate-600 text-[15px] leading-relaxed max-w-sm">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT SNIPPET */}
      <section className="py-20 sm:py-28" data-testid="home-about-snippet">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">About Us</div>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0A1A36]">
              A Fijian construction company, built on trust.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Asset Group Pte Ltd partners with homeowners, developers, and government clients
              across Fiji. Whether it&apos;s a family home in Suva or critical civil infrastructure,
              we deliver the same standard of professionalism and reliability.
            </p>
            <Link to="/about" className="inline-flex items-center mt-8 gap-2 font-bold uppercase tracking-wider text-[#0A1A36] ag-link-underline" data-testid="home-about-link">
              Learn more about us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_fiji-asset-builds/artifacts/pptux4l5_98895118-7707-4690-b153-d809cb439c8e.jpeg"
              alt="On-site work"
              className="w-full h-72 object-cover"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_fiji-asset-builds/artifacts/696v8i3e_f360641d-97b2-4a2f-b43c-7b9ba95db949.jpeg"
              alt="Civil works"
              className="w-full h-72 object-cover mt-12"
            />
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#F8FAFC] py-20 sm:py-28" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3">What We Do</div>
              <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0A1A36] max-w-xl">
                Six disciplines. One standard of excellence.
              </h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 font-bold uppercase tracking-wider text-[#0A1A36] ag-link-underline" data-testid="home-services-link">
              All services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-[#E2E8F0] bg-white">
            {SERVICES.map((s, i) => (
              <Link
                key={s.slug}
                to="/services"
                className="group relative p-8 border-r border-b border-[#E2E8F0] hover:bg-[#0A1A36] hover:text-white transition-colors duration-300"
                data-testid={`home-service-card-${s.slug}`}
              >
                <div className="text-xs font-mono text-[#D4AF37]">0{i + 1}</div>
                <ServiceIcon slug={s.slug} />
                <h3 className="font-display text-xl font-bold mt-4">{s.title}</h3>
                <p className="mt-3 text-sm text-slate-600 group-hover:text-white/75">{s.short}</p>
                <ArrowRight className="absolute bottom-6 right-6 h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="relative bg-[#0A1A36] text-white py-20 sm:py-28 overflow-hidden" data-testid="home-why-choose">
        <div className="absolute inset-0 ag-grid-lines opacity-40" />
        <div className="absolute -top-20 -right-20 h-80 w-80 border border-[#D4AF37]/20 rotate-12" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-end mb-14">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3">Why Choose Us</div>
              <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
                The difference is in <span className="text-[#D4AF37]">how</span> we build.
              </h2>
            </div>
            <p className="lg:col-span-5 text-white/75 leading-relaxed">
              Choosing a builder is a long-term decision. Here&apos;s what clients across Fiji rely on us for —
              every project, every site, every time.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#D4AF37]/20">
            {[
              {
                icon: Award,
                num: "01",
                title: "Local Expertise",
                body: "Born and built in Fiji. We understand the terrain, climate and compliance — and we build to suit them.",
              },
              {
                icon: BadgeCheck,
                num: "02",
                title: "Quality, Uncompromised",
                body: "Disciplined craft, durable materials, and finishes that hold up to tropical conditions for decades.",
              },
              {
                icon: Clock,
                num: "03",
                title: "On Time, On Budget",
                body: "Honest programmes and clear cost estimates. We deliver what we quote, when we say we will.",
              },
              {
                icon: Users,
                num: "04",
                title: "Experienced Team",
                body: "From foundation crew to project managers, our people bring practical know-how and a safety-first culture.",
              },
            ].map((item) => (
              <div
                key={item.num}
                className="bg-[#0A1A36] p-8 hover:bg-[#0F2347] transition-colors duration-300 group"
                data-testid={`why-choose-card-${item.num}`}
              >
                <div className="flex items-start justify-between">
                  <item.icon className="h-8 w-8 text-[#D4AF37]" strokeWidth={1.75} />
                  <span className="text-xs font-mono text-white/30 group-hover:text-[#D4AF37] transition-colors">{item.num}</span>
                </div>
                <h3 className="font-display text-xl font-bold mt-6">{item.title}</h3>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="py-20 sm:py-28" data-testid="home-projects">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3">Recent Work</div>
              <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0A1A36]">
                Built across Fiji.
              </h2>
            </div>
            <Link to="/projects" className="inline-flex items-center gap-2 font-bold uppercase tracking-wider text-[#0A1A36] ag-link-underline" data-testid="home-projects-link">
              All projects <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(featured.length ? featured : projects.slice(0, 3)).map((p) => (
              <article key={p.id} className="group" data-testid={`home-project-${p.id}`}>
                <div className="overflow-hidden">
                  <img
                    src={p.image_url}
                    alt={p.title}
                    className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">{p.category}</div>
                  <h3 className="font-display text-lg font-bold mt-1 text-[#0A1A36]">{p.title}</h3>
                  <div className="mt-1 text-sm text-slate-500 flex items-center gap-2">
                    <MapPin className="h-3 w-3" /> {p.location || "Fiji"}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* QUICK CONTACT STRIP */}
      <section className="py-20 sm:py-24 bg-[#F8FAFC]" data-testid="home-contact-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3">Get In Touch</div>
              <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0A1A36]">
                Reach out — we&apos;re ready to help.
              </h2>
              <p className="mt-5 text-slate-600 leading-relaxed max-w-md">
                Call, email, or drop into the office on MSC Road. We respond to every enquiry within one business day.
              </p>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-[#E2E8F0]">
              <ContactCard
                icon={Phone}
                label="Phone"
                value={COMPANY.phone}
                sub={COMPANY.contactPerson}
                href={`tel:${COMPANY.phoneRaw}`}
                testId="strip-phone"
              />
              <ContactCard
                icon={Mail}
                label="Email"
                value={COMPANY.email}
                sub="Replies within 1 business day"
                href={`mailto:${COMPANY.email}`}
                testId="strip-email"
              />
              <ContactCard
                icon={MapPin}
                label="Office"
                value={COMPANY.address}
                sub="Suva, Fiji"
                testId="strip-address"
              />
              <ContactCard
                icon={Clock}
                label="Office Hours"
                value="Mon – Fri"
                sub="8:00 AM – 5:00 PM"
                testId="strip-hours"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#0A1A36] text-white py-20 sm:py-28 ag-noise" data-testid="home-cta">
        <div className="absolute inset-0 ag-grid-lines opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
              Planning a build? Let&apos;s draw up the plan together.
            </h2>
            <p className="mt-4 text-white/80 max-w-2xl">
              Tell us about your project — we&apos;ll come back with a clear estimate and a realistic programme.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <Link to="/quote" data-testid="home-cta-quote">
              <Button className="rounded-none h-12 px-7 bg-[#D4AF37] hover:bg-[#b8952b] text-[#0A1A36] font-bold uppercase tracking-wider ag-btn-hover">
                Request a Quote
              </Button>
            </Link>
            <Link to="/contact" data-testid="home-cta-contact">
              <Button variant="outline" className="rounded-none h-12 px-7 bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0A1A36] font-bold uppercase tracking-wider ag-btn-hover">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between gap-3 border-b border-white/10 pb-3">
    <span className="text-white/60 text-xs uppercase tracking-[0.2em]">{k}</span>
    <span className="text-white font-medium">{v}</span>
  </div>
);

const ContactCard = ({ icon: Icon, label, value, sub, href, testId }) => {
  const Wrapper = href ? "a" : "div";
  const wrapperProps = href ? { href } : {};
  return (
    <Wrapper
      {...wrapperProps}
      data-testid={testId}
      className="group bg-white p-7 hover:bg-[#0A1A36] hover:text-white transition-colors duration-300 block"
    >
      <div className="flex items-center justify-between">
        <div className="h-11 w-11 flex items-center justify-center bg-[#0A1A36] text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#0A1A36] transition-colors">
          <Icon className="h-5 w-5" />
        </div>
        <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400 group-hover:text-[#D4AF37]">{label}</span>
      </div>
      <div className="mt-5 font-display text-lg font-bold text-[#0A1A36] group-hover:text-white break-words">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500 group-hover:text-white/70">{sub}</div>}
    </Wrapper>
  );
};

const ServiceIcon = ({ slug }) => {
  const map = {
    residential: HardHat,
    commercial: Building2,
    renovations: Hammer,
    "civil-works": ShieldCheck,
    "project-management": ShieldCheck,
    consultancy: ShieldCheck,
  };
  const Icon = map[slug] || Building2;
  return <Icon className="h-7 w-7 mt-4 text-[#D4AF37]" strokeWidth={1.75} />;
};
